import { useMemo, useState } from 'react';
import {
  Database,
  Pencil,
  Plus,
  RefreshCw,
  RotateCcw,
  Search,
  Trash2,
  Upload,
} from 'lucide-react';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { useSession } from '@/stores/session';
import { DocumentStatusBadge } from '../components/DocumentStatusBadge';
import { KnowledgeBaseDialog } from '../components/KnowledgeBaseDialog';
import { UploadDocumentDialog } from '../components/UploadDocumentDialog';
import {
  useDeleteKnowledgeBase,
  useDeleteKnowledgeDocument,
  useKnowledgeBases,
  useKnowledgeDocuments,
  useKnowledgeSearch,
  useResyncKnowledgeDocument,
  useSyncKnowledgeBase,
} from '../queries';
import type { KnowledgeBase, KnowledgeChunk, KnowledgeDocument } from '../types';

const SCOPE_LABELS: Record<KnowledgeBase['scope'], string> = {
  org: 'Organization',
  project: 'Project',
  agent: 'Agent',
  direction: 'Direction',
};

export function KnowledgeBasePage() {
  // Curation (create/edit/delete bases, delete documents) is Founder/Super-Admin
  // only — mirrors the server gate (DEV13-49). Upload/search/re-sync stay staff-wide.
  const role = useSession((s) => s.role);
  const canManage = role === 'founder' || role === 'super_admin';
  const basesQuery = useKnowledgeBases({ is_archived: false });
  const bases = useMemo(() => basesQuery.data?.results ?? [], [basesQuery.data]);

  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [dialogBase, setDialogBase] = useState<KnowledgeBase | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const selected =
    (selectedId != null ? bases.find((b) => b.id === selectedId) : null) ?? bases[0] ?? null;

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight">
            <Database className="size-6" /> Knowledge Base
          </h1>
          <p className="text-muted-foreground text-sm">
            Org-wide RAG corpora. Upload documents and search them semantically.
          </p>
        </div>
        {canManage ? (
          <Button
            onClick={() => {
              setDialogBase(null);
              setDialogOpen(true);
            }}
          >
            <Plus className="size-4" /> New base
          </Button>
        ) : null}
      </div>

      <div className="grid gap-6 lg:grid-cols-[18rem_1fr]">
        {/* Bases list */}
        <aside className="space-y-2">
          {basesQuery.isPending ? (
            <Skeleton className="h-40 w-full" />
          ) : bases.length === 0 ? (
            <p className="text-muted-foreground text-sm">No knowledge bases yet.</p>
          ) : (
            bases.map((b) => (
              <Card
                key={b.id}
                className={cn(
                  'cursor-pointer gap-0 py-0 transition-colors',
                  selected?.id === b.id ? 'border-primary' : 'hover:border-muted-foreground/40',
                )}
                onClick={() => setSelectedId(b.id)}
              >
                <CardContent className="flex items-center gap-2 px-3 py-2">
                  <span className="min-w-0 flex-1 truncate text-sm font-medium">{b.name}</span>
                  <span className="text-muted-foreground shrink-0 text-xs">
                    {SCOPE_LABELS[b.scope]} · {b.document_count}
                  </span>
                  {!b.is_active ? (
                    <Badge variant="secondary" className="shrink-0">
                      Inactive
                    </Badge>
                  ) : null}
                </CardContent>
              </Card>
            ))
          )}
        </aside>

        {/* Selected base detail */}
        <div className="min-w-0">
          {selected ? (
            <KnowledgeBaseDetail
              canManage={canManage}
              base={selected}
              onEdit={() => {
                setDialogBase(selected);
                setDialogOpen(true);
              }}
            />
          ) : (
            <div className="text-muted-foreground rounded-md border border-dashed p-10 text-center text-sm">
              Select or create a knowledge base to get started.
            </div>
          )}
        </div>
      </div>

      <KnowledgeBaseDialog
        key={`${dialogBase?.id ?? 'new'}:${dialogOpen}`}
        base={dialogBase}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onCreated={(b) => setSelectedId(b.id)}
      />
    </div>
  );
}

function KnowledgeBaseDetail({
  base,
  canManage,
  onEdit,
}: {
  base: KnowledgeBase;
  canManage: boolean;
  onEdit: () => void;
}) {
  const documentsQuery = useKnowledgeDocuments(base.id);
  const documents = documentsQuery.data?.results ?? [];
  const syncBase = useSyncKnowledgeBase();
  const resyncDoc = useResyncKnowledgeDocument();
  const deleteDoc = useDeleteKnowledgeDocument();
  const deleteBase = useDeleteKnowledgeBase();

  const [uploadOpen, setUploadOpen] = useState(false);
  const [confirmDeleteBase, setConfirmDeleteBase] = useState(false);
  const [pendingDeleteDoc, setPendingDeleteDoc] = useState<KnowledgeDocument | null>(null);

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="truncate text-lg font-semibold">{base.name}</h2>
          {base.description ? (
            <p className="text-muted-foreground text-sm">{base.description}</p>
          ) : null}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {canManage ? (
            <Button variant="outline" size="sm" onClick={onEdit}>
              <Pencil className="size-4" /> Edit
            </Button>
          ) : null}
          <Button
            variant="outline"
            size="sm"
            onClick={() => syncBase.mutate(base.public_id)}
            disabled={syncBase.isPending}
          >
            <RefreshCw className="size-4" /> Re-sync base
          </Button>
          {canManage ? (
            <Button
              variant="outline"
              size="sm"
              className="text-destructive"
              onClick={() => setConfirmDeleteBase(true)}
            >
              <Trash2 className="size-4" /> Delete
            </Button>
          ) : null}
        </div>
      </div>

      {/* Search */}
      <SearchPanel knowledgeBaseId={base.id} />

      {/* Documents */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold">Documents</h3>
          <Button size="sm" onClick={() => setUploadOpen(true)}>
            <Upload className="size-4" /> Upload
          </Button>
        </div>

        {documentsQuery.isPending ? (
          <Skeleton className="h-32 w-full" />
        ) : documents.length === 0 ? (
          <p className="text-muted-foreground rounded-md border border-dashed p-6 text-center text-sm">
            No documents yet. Upload a file to index it.
          </p>
        ) : (
          <div className="overflow-x-auto rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Title</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Chunks</TableHead>
                  <TableHead>Last synced</TableHead>
                  <TableHead className="w-24" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {documents.map((doc) => (
                  <TableRow key={doc.id}>
                    <TableCell>
                      <div className="font-medium">{doc.title}</div>
                      {doc.error ? (
                        <div className="text-destructive text-xs" title={doc.error}>
                          {doc.error}
                        </div>
                      ) : (
                        <div className="text-muted-foreground text-xs">{doc.mime || doc.source_kind}</div>
                      )}
                    </TableCell>
                    <TableCell>
                      <DocumentStatusBadge status={doc.status} />
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{doc.chunk_count}</TableCell>
                    <TableCell className="text-muted-foreground text-xs">
                      {doc.last_synced_at ? new Date(doc.last_synced_at).toLocaleString() : '—'}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          size="icon"
                          variant="ghost"
                          aria-label={`Re-sync ${doc.title}`}
                          title="Re-index"
                          disabled={resyncDoc.isPending}
                          onClick={() => resyncDoc.mutate(doc.public_id)}
                        >
                          <RotateCcw className="size-4" />
                        </Button>
                        {canManage ? (
                          <Button
                            size="icon"
                            variant="ghost"
                            aria-label={`Delete ${doc.title}`}
                            className="text-destructive"
                            onClick={() => setPendingDeleteDoc(doc)}
                          >
                            <Trash2 className="size-4" />
                          </Button>
                        ) : null}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      <UploadDocumentDialog
        key={`${base.id}:${uploadOpen}`}
        knowledgeBaseId={base.id}
        open={uploadOpen}
        onOpenChange={setUploadOpen}
      />

      <AlertDialog open={confirmDeleteBase} onOpenChange={setConfirmDeleteBase}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete “{base.name}”?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the knowledge base and all its indexed documents. This
              cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => deleteBase.mutate(base.public_id)}>
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={pendingDeleteDoc !== null}
        onOpenChange={(o) => !o && setPendingDeleteDoc(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete “{pendingDeleteDoc?.title}”?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes the document and its chunks from the index. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (pendingDeleteDoc) deleteDoc.mutate(pendingDeleteDoc.public_id);
                setPendingDeleteDoc(null);
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function SearchPanel({ knowledgeBaseId }: { knowledgeBaseId: number }) {
  const search = useKnowledgeSearch();
  const [query, setQuery] = useState('');

  const runSearch = () => {
    const q = query.trim();
    if (!q) return;
    search.mutate({ query: q, knowledge_base_ids: [knowledgeBaseId], k: 10 });
  };

  // 503 = embedder not configured; surface a gentle notice rather than an error.
  const unavailable = search.error?.status === 503;
  const results: KnowledgeChunk[] = search.data ?? [];

  return (
    <div className="bg-muted/30 space-y-3 rounded-md border p-4">
      <div className="flex items-center gap-2">
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') runSearch();
          }}
          placeholder="Search this knowledge base…"
          className="h-9"
        />
        <Button size="sm" onClick={runSearch} disabled={search.isPending || !query.trim()}>
          <Search className="size-4" /> Search
        </Button>
      </div>

      {unavailable ? (
        <p className="text-muted-foreground text-sm">
          Search is unavailable — no embedding model is configured for this organization.
        </p>
      ) : search.isPending ? (
        <Skeleton className="h-16 w-full" />
      ) : search.isSuccess && results.length === 0 ? (
        <p className="text-muted-foreground text-sm">No matching passages found.</p>
      ) : results.length > 0 ? (
        <ul className="space-y-2">
          {results.map((chunk) => (
            <li key={chunk.id} className="bg-background rounded-md border p-3 text-sm">
              <p className="whitespace-pre-wrap">{chunk.text}</p>
              {chunk.tags.length > 0 ? (
                <div className="mt-2 flex flex-wrap gap-1">
                  {chunk.tags.map((t) => (
                    <Badge key={t} variant="outline" className="font-normal">
                      {t}
                    </Badge>
                  ))}
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
